import { TextSearch } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import useNextUrlState from '../../../../hooks/useNextUrlState';
import type { NoteSearchType } from './types';

interface NoteSearch {
  searchKeyword: string;
  searchType: NoteSearchType;
}

interface NoteSearchOption {
  label: string;
  value: NoteSearchType;
}

interface NoteSearchBarProps {
  isMobile?: boolean;
}

export const NoteSearchBar = ({ isMobile = false }: NoteSearchBarProps) => {
  const { t } = useTranslation();
  const { isKo } = useLang();

  const { value: query, fetchQuery } = useNextUrlState<Partial<NoteSearch>>();
  const form = useForm<NoteSearch>({
    defaultValues: query,
  });

  const NOTE_SEARCH_TYPE: NoteSearchOption[] = [
    { label: t('전체'), value: 'ALL' },
    { label: t('강의명'), value: 'COURSE' },
    { label: t('노트 내용'), value: 'NOTE' },
  ];

  const handleSubmit: SubmitHandler<NoteSearch> = ({ searchKeyword, searchType }) => {
    fetchQuery({ searchKeyword, searchType });
  };

  const handleClear = () => {
    /** reset 사용할 경우 searchType도 defaultValue로 reset 되는 이슈가 있어 setValue로 searchKeyword만 clear합니다.*/
    form.setValue('searchKeyword', '');
  };

  useEffect(() => {
    form.setValue('searchKeyword', query.searchKeyword ?? '');
  }, [form, query.searchKeyword]);

  return (
    <TextSearch
      size={isMobile ? 'md' : 'sm'}
      css={{ '& > div': { width: '100%' } }}
      component="form"
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Controller
        name="searchType"
        control={form.control}
        render={({ field }) => (
          <TextSearch.Select
            w={(isKo ? 110 : 130) + (isMobile ? 14 : 0)}
            styles={{ input: { paddingRight: 0 } }}
            data={NOTE_SEARCH_TYPE}
            defaultValue={query.searchType ?? NOTE_SEARCH_TYPE[0].value}
            {...field}
          />
        )}
      />

      <Controller
        name="searchKeyword"
        control={form.control}
        render={({ field }) => (
          <TextSearch.TextInput
            {...field}
            placeholder={t('(으)로 검색')}
            w={176}
            onClear={handleClear}
          />
        )}
      />

      <TextSearch.ActionIcon type="submit" />
    </TextSearch>
  );
};
